import {FC, useCallback, useEffect} from 'react';
import {FormProvider, useForm, useFormContext} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import Checkboxes from 'components/Form/Checkboxes';
import Modal from 'components/Modal';
import Notification from 'components/Notification';
import TooltipWrapper from 'components/Tooltip';
import PdfFilenameComponentName from '../../common/PdfFilenameComponentName';
import {PdfFilenameSettingsFormFields} from '../../services';
import PDF_FILENAME_ELEMENTS, {
    PdfFilenameElementOption,
    PdfFilenameSectionName,
} from '../../types';

type AddItemModalForm = {
    modal: Record<PdfFilenameElementOption, boolean>;
};

const DEFAULT_VALUES = {
    modal: Object.fromEntries(
        PDF_FILENAME_ELEMENTS.map((element) => [element, true]),
    ),
};

type AddItemModalProps = {
    isOpen: boolean;
    onClose: () => void;
    sectionName: PdfFilenameSectionName;
};

const AddItemModal: FC<AddItemModalProps> = ({
    isOpen,
    onClose,
    sectionName,
}) => {
    const {t} = useTranslation('Document.PdfFilenameSettings');

    const {getValues, setValue} =
        useFormContext<PdfFilenameSettingsFormFields>();
    const methods = useForm<AddItemModalForm>({defaultValues: DEFAULT_VALUES});

    const selection = methods.watch('modal');
    const numberSelected = Object.values(selection).filter(Boolean).length;

    // Set Initial Modal Value
    useEffect(() => {
        if (isOpen) {
            const initialSelection = [...getValues()[sectionName]];

            const newValue = Object.fromEntries(
                PDF_FILENAME_ELEMENTS.map((element) => [
                    element,
                    initialSelection.includes(element),
                ]),
            ) as AddItemModalForm['modal'];

            methods.setValue('modal', newValue);
        }
    }, [getValues, isOpen, methods, sectionName, setValue]);

    const onCancelModal = onClose;

    const onCloseModal = useCallback(() => {
        const initialSelection = [...getValues()[sectionName]];

        const existingElements = initialSelection.filter(
            (element) => selection[element],
        );
        const addedElements = PDF_FILENAME_ELEMENTS.filter(
            (element) =>
                selection[element] && !existingElements.includes(element),
        );

        setValue(sectionName, [...existingElements, ...addedElements]);
        onClose();
    }, [getValues, onClose, sectionName, selection, setValue]);

    const options = PDF_FILENAME_ELEMENTS.map((element) => ({
        label: <PdfFilenameComponentName element={element} />,
        name: `modal.${element}`,
    }));

    return (
        <FormProvider {...methods}>
            <Modal isOpen={isOpen} onClose={onCancelModal}>
                <Modal.Header>{t('addItemModal.title')}</Modal.Header>
                <Modal.Body>
                    {numberSelected === 0 && (
                        <Notification
                            className="mb-4"
                            icon="warning"
                            size="sm"
                            type="alert"
                        >
                            {t('message.noItem')}
                        </Notification>
                    )}
                    <Checkboxes options={options} />
                </Modal.Body>
                <Modal.Footer>
                    <Modal.Button
                        kind="tertiary"
                        onClick={onCancelModal}
                        size="sm"
                        type="cancel"
                    >
                        {t('form:cancel')}
                    </Modal.Button>
                    <TooltipWrapper
                        message={
                            numberSelected === 0 ? t('message.noItem') : ''
                        }
                    >
                        <Modal.Button
                            disabled={numberSelected === 0}
                            onClick={onCloseModal}
                            size="sm"
                            type="submit"
                        >
                            {t('form:select')}
                        </Modal.Button>
                    </TooltipWrapper>
                </Modal.Footer>
            </Modal>
        </FormProvider>
    );
};

export default AddItemModal;
